import { createRouter, createWebHistory } from 'vue-router'
import { ElMessage } from 'element-plus'

// 路由配置
const routes = [
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/login',
    name: 'AdminLogin',
    component: () => import('../views/AdminLogin.vue'),
    meta: { title: '管理员登录' }
  },
  {
    path: '/records',
    name: 'Records',
    component: () => import('../views/Records.vue'),
    meta: { title: '记录列表', requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/upload',
    name: 'Upload',
    component: () => import('../views/Upload.vue'),
    meta: { title: '上传记录' }
  },
  {
    path: '/search',
    name: 'Search',
    component: () => import('../views/Search.vue'),
    meta: { title: '搜索查询' }
  },
  {
    path: '/record/:id',
    name: 'RecordDetail',
    component: () => import('../views/RecordDetail.vue'),
    meta: { title: '记录详情' }
  },
  {
    path: '/admin',
    name: 'AdminPanel',
    component: () => import('../views/AdminPanel.vue'),
    meta: { title: '用户管理', requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/mobile',
    component: () => import('../views/mobile/MobileLayout.vue'),
    children: [
      {
        path: '',
        name: 'Mobile',
        redirect: '/mobile/home'
      },
      {
        path: 'home',
        name: 'MobileHome',
        component: () => import('../views/mobile/Home.vue'),
        meta: { title: '首页', keepAlive: true }
      },
      {
        path: 'upload',
        name: 'MobileUpload',
        component: () => import('../views/mobile/Upload.vue'),
        meta: { title: '上传环志信息' }
      },
      {
        path: 'records',
        name: 'MobileRecords',
        component: () => import('../views/mobile/Records.vue'),
        meta: { title: '我的记录' }
      },
      {
        path: 'record/:id',
        name: 'MobileRecordDetail',
        component: () => import('../views/mobile/RecordDetail.vue'),
        meta: { title: '记录详情' }
      },
      {
        path: 'profile',
        name: 'MobileProfile',
        component: () => import('../views/mobile/Profile.vue'),
        meta: { title: '个人中心' }
      }
    ]
  }
]

// 创建路由实例
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

// 路由守卫 - 权限检查和页面标题
router.beforeEach((to, from, next) => {
  // 防止无限重定向：如果从登录页跳转到登录页，直接允许
  if (to.path === '/login' && from.path === '/login') {
    next()
    return
  }
  
  // 防止无限重定向：如果从记录页跳转到记录页，直接允许
  if (to.path === '/records' && from.path === '/records') {
    next()
    return
  }
  
  // 设置页面标题
  if (to.meta.title) {
    document.title = `${to.meta.title} - 鸟类环志管理后台`
  }

  // 检查是否需要登录
  if (to.meta.requiresAuth) {
    const token = localStorage.getItem('token')
    const userInfo = localStorage.getItem('userInfo')
    
    if (!token || !userInfo) {
      // 未登录，跳转到登录页
      console.log('🔒 未登录，跳转到登录页')
      if (from.path !== '/login') {
        ElMessage.warning('请先登录')
      }
      next('/login')
      return
    }
    
    // 检查是否为管理员
    if (to.meta.requiresAdmin) {
      try {
        const user = JSON.parse(userInfo)
        // 🔧 修复：后端返回的roles是字符串数组，不是对象数组
        const isAdmin = user.roles && (
          user.roles.includes('ROLE_ADMIN') ||
          user.roles.includes('ROLE_SUPER_ADMIN') ||
          // 兼容对象格式（如果有的话）
          (Array.isArray(user.roles) && user.roles.some(role => {
            if (typeof role === 'string') {
              return role === 'ROLE_ADMIN' || role === 'ROLE_SUPER_ADMIN'
            } else if (role && role.name) {
              return role.name === 'ROLE_ADMIN' || role.name === 'ROLE_SUPER_ADMIN'
            }
            return false
          }))
        )
        
        if (!isAdmin) {
          console.warn('❌ 用户不是管理员:', {
            username: user.username,
            roles: user.roles,
            userInfo: user
          })
          ElMessage.error('需要管理员权限')
          // 清除无效的登录信息
          localStorage.removeItem('token')
          localStorage.removeItem('userInfo')
          localStorage.removeItem('refreshToken')
          if (from.path !== '/login') {
            next('/login')
          } else {
            // 如果已经在登录页，不要再跳转
            next()
          }
          return
        } else {
          console.log('✅ 管理员权限验证通过:', {
            username: user.username,
            roles: user.roles
          })
        }
      } catch (error) {
        console.error('解析用户信息失败:', error, userInfo)
        // 清除无效的登录信息
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        localStorage.removeItem('refreshToken')
        next('/login')
        return
      }
    }
  }
  
  // 如果已登录且是管理员，访问登录页时跳转到记录列表
  if (to.path === '/login') {
    const token = localStorage.getItem('token')
    const userInfo = localStorage.getItem('userInfo')
    
    if (token && userInfo) {
      try {
        const user = JSON.parse(userInfo)
        // 🔧 修复：检查是否为管理员
        const isAdmin = user.roles && (
          user.roles.includes('ROLE_ADMIN') ||
          user.roles.includes('ROLE_SUPER_ADMIN') ||
          (Array.isArray(user.roles) && user.roles.some(role => {
            if (typeof role === 'string') {
              return role === 'ROLE_ADMIN' || role === 'ROLE_SUPER_ADMIN'
            } else if (role && role.name) {
              return role.name === 'ROLE_ADMIN' || role.name === 'ROLE_SUPER_ADMIN'
            }
            return false
          }))
        )
        
        if (isAdmin) {
          next('/records')
          return
        } else {
          // 不是管理员，清除登录信息
          localStorage.removeItem('token')
          localStorage.removeItem('userInfo')
          localStorage.removeItem('refreshToken')
        }
      } catch (error) {
        console.error('检查登录状态失败:', error)
        // 解析失败，清除登录信息
        localStorage.removeItem('token')
        localStorage.removeItem('userInfo')
        localStorage.removeItem('refreshToken')
      }
    }
  }

  // 检测设备类型并重定向移动端用户（仅非管理后台页面）
  if (!to.meta.requiresAdmin) {
    const isMobile = window.innerWidth <= 768
    if (isMobile && !to.path.startsWith('/mobile')) {
      next('/mobile')
      return
    }
  }

  next()
})

export default router
